import {
    isLegacyPuzzleUiHash,
    PUZZLE_VIEW_PARAM,
    shouldStripSearchParamOnPuzzleListingNavigation,
} from "@/helpers/puzzle-view-url";

const PUZZLE_DATE_REGEX = /^\d{6}$/;

export function encodePuzzleLonglinkSegment(segment: string): string {
    return encodeURIComponent(segment);
}

export function decodePuzzleLonglinkSegment(segment: string): string {
    try {
        return decodeURIComponent(segment);
    } catch {
        return segment;
    }
}

function splitPathSearchHash(input: string): { path: string; search: string; hash: string } {
    const hashIndex = input.indexOf("#");
    const beforeHash = hashIndex >= 0 ? input.slice(0, hashIndex) : input;
    const hash = hashIndex >= 0 ? input.slice(hashIndex) : "";
    const searchIndex = beforeHash.indexOf("?");
    const path = searchIndex >= 0 ? beforeHash.slice(0, searchIndex) : beforeHash;
    const search = searchIndex >= 0 ? beforeHash.slice(searchIndex) : "";
    return { path, search, hash };
}

/**
 * Encode only `/puzzle/{date}/{longlink}` longlink while preserving query params.
 * Also handles legacy URLs where slug continuation/query accidentally live in hash.
 */
export function encodePuzzleLonglinkInUrl(inputUrl: string): string {
    const withoutOrigin = inputUrl.replace(/^https?:\/\/[^/]+/i, "");
    const { path, search, hash } = splitPathSearchHash(withoutOrigin || "/");
    const parts = path.split("/").filter(Boolean);
    if (parts.length < 3 || parts[0] !== "puzzle" || !PUZZLE_DATE_REGEX.test(parts[1])) {
        return `${path}${search}${hash}`;
    }

    const datePart = parts[1];
    let longlink = parts.slice(2).join("/");
    let outHash = hash;

    const hashRaw = outHash.replace(/^#/, "");
    const isUiHash = isLegacyPuzzleUiHash(hashRaw);
    if (hashRaw && !isUiHash) {
        longlink = `${longlink}#${hashRaw}`;
        outHash = "";
    }

    const encodedLonglink = encodePuzzleLonglinkSegment(longlink);
    return `/puzzle/${datePart}/${encodedLonglink}${search}${outHash}`;
}

export function withCurrentSearchParams(path: string): string {
    if (typeof window === "undefined") {
        return path;
    }

    const encodedPath = encodePuzzleLonglinkInUrl(path);

    const incomingSearch = window.location.search;
    if (!incomingSearch || incomingSearch === "?") {
        return encodedPath;
    }

    const { path: basePath, search: existingSearch, hash } = splitPathSearchHash(encodedPath);
    const merged = new URLSearchParams(existingSearch.startsWith("?") ? existingSearch.slice(1) : existingSearch);
    const incoming = new URLSearchParams(incomingSearch.startsWith("?") ? incomingSearch.slice(1) : incomingSearch);
    incoming.forEach((value, key) => {
        if (shouldStripSearchParamOnPuzzleListingNavigation(key)) {
            return;
        }
        merged.set(key, value);
    });

    const mergedSearch = merged.toString();
    return `${basePath}${mergedSearch ? `?${mergedSearch}` : ""}${hash}`;
}

/**
 * Opens a puzzle listing with `?view=listing`, preserving marketing params but not feed `pageN` or prior `view`.
 */
export function buildPuzzleListingNavigationUrl(path: string): string {
    const encodedPath = encodePuzzleLonglinkInUrl(path);
    const { path: basePath, search: existingSearch, hash } = splitPathSearchHash(encodedPath);
    const params = new URLSearchParams(existingSearch.startsWith("?") ? existingSearch.slice(1) : existingSearch);
    if (typeof window !== "undefined") {
        const incoming = new URLSearchParams(
            window.location.search.startsWith("?") ? window.location.search.slice(1) : window.location.search,
        );
        incoming.forEach((value, key) => {
            if (shouldStripSearchParamOnPuzzleListingNavigation(key)) {
                return;
            }
            params.set(key, value);
        });
    }
    params.set(PUZZLE_VIEW_PARAM, "listing");
    const mergedSearch = params.toString();
    const legacyHash = hash.replace(/^#/, "");
    const keepHash = legacyHash && !isLegacyPuzzleUiHash(legacyHash) ? hash : "";
    return `${basePath}${mergedSearch ? `?${mergedSearch}` : ""}${keepHash}`;
}
